import { useState, useCallback, useRef } from 'react';
import { useWiki } from './WikiContext';
import type { HealResult, ReembedResult } from '@equationalapplications/core-llm-wiki';

export type MaintenanceResult =
  | { operation: 'librarian'; result: undefined }
  | { operation: 'heal'; result: HealResult }
  | { operation: 'prune'; result: { entries: number; tasks: number; events: number } };

export function useWikiMaintenance() {
  const wiki = useWiki();
  const wikiRef = useRef(wiki);
  wikiRef.current = wiki;

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastResult, setLastResult] = useState<MaintenanceResult | null>(null);
  // Counter so any overlapping maintenance operation keeps isPending=true until all complete
  const pendingCount = useRef(0);

  const runLibrarian = useCallback(async (entityId: string, options?: { promptOverride?: string }): Promise<void> => {
    setError(null);
    pendingCount.current += 1;
    setIsPending(true);
    setLastResult(null);
    try {
      await (options ? wikiRef.current.runLibrarian(entityId, options) : wikiRef.current.runLibrarian(entityId));
      setLastResult({ operation: 'librarian', result: undefined });
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      pendingCount.current -= 1;
      if (pendingCount.current === 0) setIsPending(false);
    }
  }, []);

  const runHeal = useCallback(async (
    entityId: string,
    options?: { promptOverride?: string; batchSize?: number },
  ): Promise<HealResult> => {
    setError(null);
    pendingCount.current += 1;
    setIsPending(true);
    setLastResult(null);
    try {
      const result = await (options
        ? wikiRef.current.runHeal(entityId, options)
        : wikiRef.current.runHeal(entityId));
      setLastResult({ operation: 'heal', result });
      return result;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      pendingCount.current -= 1;
      if (pendingCount.current === 0) setIsPending(false);
    }
  }, []);

  const runPrune = useCallback(
    async (
      entityId: string,
      options?: {
        retainSoftDeletedFor?: number | null;
        retainEventsFor?: number | null;
        vacuum?: boolean;
      }
    ): Promise<{ entries: number; tasks: number; events: number }> => {
      setError(null);
      pendingCount.current += 1;
      setIsPending(true);
      setLastResult(null);
      try {
        const result = await wikiRef.current.runPrune(entityId, options);
        setLastResult({ operation: 'prune', result });
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        pendingCount.current -= 1;
        if (pendingCount.current === 0) setIsPending(false);
      }
    },
    []
  );

  const runReembed = useCallback(async (entityId?: string, opts?: { force?: boolean; skipExisting?: boolean }): Promise<ReembedResult> => {
    setError(null);
    pendingCount.current += 1;
    setIsPending(true);
    setLastResult(null);
    try {
      // runReembed returns its result directly; it does not update the shared
      // MaintenanceResult/lastResult field (which only tracks librarian, heal, prune).
      // Using the return value directly avoids widening the MaintenanceResult union
      // in a way that would break existing consumers that exhaustively switch on
      // lastResult.operation.
      return await wikiRef.current.runReembed(entityId, opts);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      pendingCount.current -= 1;
      if (pendingCount.current === 0) setIsPending(false);
    }
  }, []);

  return { runLibrarian, runHeal, runPrune, runReembed, lastResult, isPending, error };
}
